import {useMemo} from 'react';

import * as QueryTS from '../../../../../util/queryts';
import {type Sort} from '../../../../../util/queryTypes';
import {YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {
  type ConfigValueForRun,
  type ConfigValuesNumberAggregation,
  useConfigValuesAggregation,
} from './useConfigValuesAggregation';
import {useConfigValuesCategories} from './useConfigValuesCategories';
import {
  type MetricStatisticsAggregation,
  useMetricStatisticsAggregation,
} from './useMetricStatisticsAggregation';
import {useMetricStatisticsForRun} from './useMetricStatisticsForRun';

export type SemanticLegendRuntimeDataKeySource = 'metric' | 'config';

type UseSemanticLegendRuntimeDataArgs = {
  currentKey?: string;
  enabled: boolean;
  entityName: string;
  filters: string;
  keySource?: SemanticLegendRuntimeDataKeySource;
  order: Sort;
  projectName: string;
  yAxisValue?: YAxisValues;
};

export type SemanticLegendRuntimeData = {
  configValuesCategories: string[];
  configValuesForRun: ConfigValueForRun[];
  configValuesForRunCategories: ConfigValueForRun[];
  configValuesNumberAggregation: ConfigValuesNumberAggregation;
  error?: Error;
  loading: boolean;
  metricStatisticsAggregation: MetricStatisticsAggregation;
  metricStatisticsForRun: Array<{
    last: number;
    max: number;
    min: number;
    run_name: string;
  }>;
  shouldSkipConfigValuesAggregation: boolean;
  shouldSkipConfigValuesCategories: boolean;
  shouldSkipMetricStatistics: boolean;
};

export function useSemanticLegendRuntimeData({
  currentKey,
  enabled,
  entityName,
  filters,
  keySource,
  order,
  projectName,
  yAxisValue,
}: UseSemanticLegendRuntimeDataArgs): SemanticLegendRuntimeData {
  const hasRuntimeKey =
    enabled &&
    !!currentKey &&
    currentKey !== '' &&
    currentKey !== DEFAULT_SEMANTIC_LEGEND_SETTINGS.key;
  const shouldSkipMetricStatistics = !hasRuntimeKey || keySource !== 'metric';
  const shouldSkipConfigValuesAggregation =
    !hasRuntimeKey ||
    keySource !== 'config' ||
    yAxisValue === YAxisValues.Config_Categories;
  const shouldSkipConfigValuesCategories =
    !hasRuntimeKey ||
    keySource !== 'config' ||
    yAxisValue !== YAxisValues.Config_Categories;

  const {
    metricStatisticsAggregation,
    metricStatisticsError,
    metricStatisticsLoading,
  } = useMetricStatisticsAggregation({
    currentKey,
    entityName,
    projectName,
    shouldSkipMetricStatistics,
  });

  const {
    metricStatisticsForRun,
    metricStatisticsForRunError,
    metricStatisticsForRunLoading,
  } = useMetricStatisticsForRun({
    currentKey,
    entityName,
    filters,
    order: QueryTS.sortToOrderString(order),
    projectName,
    shouldSkip: shouldSkipMetricStatistics,
  });

  const {
    configValuesForRun,
    configValuesNumberAggregation,
    configValuesError,
    configValuesLoading,
  } = useConfigValuesAggregation({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesAggregation,
  });

  const {
    configValuesForRun: configValuesForRunCategories,
    configValuesCategories,
    configValuesError: configValuesErrorCategories,
    configValuesLoading: configValuesLoadingCategories,
  } = useConfigValuesCategories({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesCategories,
  });

  const error =
    metricStatisticsError ??
    metricStatisticsForRunError ??
    configValuesError ??
    configValuesErrorCategories;
  const loading =
    (!shouldSkipMetricStatistics &&
      (metricStatisticsLoading || metricStatisticsForRunLoading)) ||
    (!shouldSkipConfigValuesAggregation && configValuesLoading) ||
    (!shouldSkipConfigValuesCategories && configValuesLoadingCategories);

  return useMemo(
    () => ({
      configValuesCategories,
      configValuesForRun,
      configValuesForRunCategories,
      configValuesNumberAggregation,
      error,
      loading,
      metricStatisticsAggregation,
      metricStatisticsForRun,
      shouldSkipConfigValuesAggregation,
      shouldSkipConfigValuesCategories,
      shouldSkipMetricStatistics,
    }),
    [
      configValuesCategories,
      configValuesForRun,
      configValuesForRunCategories,
      configValuesNumberAggregation,
      error,
      loading,
      metricStatisticsAggregation,
      metricStatisticsForRun,
      shouldSkipConfigValuesAggregation,
      shouldSkipConfigValuesCategories,
      shouldSkipMetricStatistics,
    ]
  );
}

export function isMetricStatisticYAxisValue(
  yAxisValue: YAxisValues | undefined
): yAxisValue is YAxisValues.Min | YAxisValues.Max | YAxisValues.Last {
  return (
    yAxisValue === YAxisValues.Min ||
    yAxisValue === YAxisValues.Max ||
    yAxisValue === YAxisValues.Last
  );
}
